import axios from 'axios';
import i18n from '../i18n/config';
import { getPlanUpgradeContext, shouldUsePlanUpgradeContext, savePlanUpgradeContext, clearPlanUpgradeContext } from './planUpgrade';
import { notificationService } from './notificationService';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Candidates have no account: the candidate-facing interview endpoints are protected by the secret
// invite token from the interview link. The interview page registers it here (together with the
// interview id it belongs to) and every request for that very interview then carries it in the
// X-Invite-Token header.
let interviewInvite: { interviewId: string; token: string } | null = null;

export const setInterviewInviteToken = (interviewId: string | undefined, token: string | null | undefined) => {
  interviewInvite = interviewId && token ? { interviewId, token } : null;
};

const CANDIDATE_INTERVIEW_URL = /^\/api\/(?:interviews\/(\d+)\/|upload\/video\/(\d+))/;

interface SmtpErrorInfo {
  code: string;
  message?: string;
}

const SMTP_ERROR_DEFAULT_MESSAGE =
  i18n.t('settings.email.smtpWarningDesc');

const extractSmtpError = (payload: any): SmtpErrorInfo | null => {
  if (!payload || typeof payload !== 'object') return null;

  if (payload.email_error_code) {
    return {
      code: payload.email_error_code,
      message: payload.email_error_message || payload.message,
    };
  }

  if (payload.detail && typeof payload.detail === 'object') {
    const detail = payload.detail as Record<string, any>;
    if (detail.email_error_code) {
      return {
        code: detail.email_error_code,
        message: detail.message || payload.message,
      };
    }
  }

  return null;
};

const redirectToEmailSettings = () => {
  const params = new URLSearchParams();
  params.set('tab', 'email');
  params.set('reason', 'smtp_error');

  const target = `/app/settings?${params.toString()}`;

  if (window.location.pathname.startsWith('/app/settings')) {
    const currentParams = new URLSearchParams(window.location.search);
    currentParams.set('tab', 'email');
    currentParams.set('reason', 'smtp_error');
    const newQuery = currentParams.toString();
    if (newQuery !== window.location.search.replace(/^\?/, '')) {
      window.history.replaceState({}, '', `/app/settings?${newQuery}`);
    }
  } else {
    window.location.href = target;
  }
};

const handleSmtpError = (info: SmtpErrorInfo | null) => {
  if (!info) return;
  notificationService.error(info.message || SMTP_ERROR_DEFAULT_MESSAGE, 8000);
  redirectToEmailSettings();
};

// Request interceptor to add auth token and tenant slug
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    const tenantSlug = localStorage.getItem('tenantSlug');
    const planUpgradeContext = getPlanUpgradeContext();
    const requestUrl = config.url || '';

    if (shouldUsePlanUpgradeContext(requestUrl) && planUpgradeContext?.token) {
      config.headers.Authorization = `Bearer ${planUpgradeContext.token}`;
      if (planUpgradeContext.tenantSlug) {
        config.headers['X-Tenant-Slug'] = planUpgradeContext.tenantSlug;
      }
    } else {
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      if (tenantSlug && !config.url?.includes('/public/')) {
        config.headers['X-Tenant-Slug'] = tenantSlug;
      }
    }

    if (interviewInvite) {
      const match = CANDIDATE_INTERVIEW_URL.exec(requestUrl);
      if (match && (match[1] || match[2]) === interviewInvite.interviewId) {
        config.headers['X-Invite-Token'] = interviewInvite.token;
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// The backend flags emails that failed to send during a request with `X-Email-Failed: <code>`.
// Show a warning unless the response body already reports the email result itself
// (`email_sent` / `email_error_code`), in which case the calling page handles it.
const EMAIL_FAILED_HEADER = 'x-email-failed';
const EMAIL_ERROR_CODES = ['smtp_not_configured', 'smtp_auth_failed', 'smtp_send_failed'];

const warnOnEmailFailure = (response: any) => {
  const code = response?.headers?.[EMAIL_FAILED_HEADER];
  if (!code) return response;
  const data = response.data;
  if (data && typeof data === 'object' && ('email_sent' in data || 'email_error_code' in data)) {
    return response;
  }
  const key = EMAIL_ERROR_CODES.includes(code) ? code : 'smtp_send_failed';
  notificationService.warning(i18n.t(`emailErrors.${key}`), 10000);
  return response;
};

api.interceptors.response.use(warnOnEmailFailure);
axios.interceptors.response.use(warnOnEmailFailure);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const requestUrl = error.config?.url || '';
    const smtpErrorInfo = extractSmtpError(error.response?.data);
    if (smtpErrorInfo) {
      handleSmtpError(smtpErrorInfo);
    }
    const isAuthEndpoint = [
      '/api/auth/login',
      '/api/auth/register',
      '/api/auth/password-reset-request',
      '/api/auth/password-reset-confirm'
    ].some((path) => requestUrl.includes(path));

    if (error.response?.status === 401) {
      if (isAuthEndpoint) {
        return Promise.reject(error);
      }
      // Clear auth data and redirect to login for all 401 errors
      console.warn('[API] 401 Unauthorized - Clearing auth and redirecting to login');
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('tenantSlug');
      window.location.href = '/login';
    }

    // Handle 403 Forbidden - Permission errors
    if (error.response?.status === 403) {
      const detail = error.response.data?.detail;
      const message = typeof detail === 'string' ? detail : i18n.t('team.noPermission');
      
      // Show user-friendly error message using notification banner
      notificationService.error(message);
      
      // Don't redirect, just reject the promise so the calling code can handle it
      return Promise.reject(error);
    }

    // Handle 402 Payment Required - Payment needed
    if (error.response?.status === 402) {
      const detail = error.response.data?.detail;
      
      // Enterprise payment required - redirect to payment link
      if (detail?.code === 'payment_required' && detail?.payment_link) {
        if (isAuthEndpoint) {
          return Promise.reject(error);
        }
        console.log('[API] Enterprise payment required, redirecting to payment');
        window.location.href = detail.payment_link;
        return Promise.reject(error);
      }
      
      // Other payment requirements - redirect to plan upgrade
      if (isAuthEndpoint) {
        return Promise.reject(error);
      }
      
      const message = typeof detail === 'string' ? detail : detail?.message || i18n.t('errors.paymentRequired');

      if (detail?.plan_upgrade_token) {
        savePlanUpgradeContext({
          token: detail.plan_upgrade_token,
          tenantSlug: detail?.tenant?.slug,
          tenantId: detail?.tenant?.id,
          tenantName: detail?.tenant?.name,
        });
      } else {
        clearPlanUpgradeContext();
      }

      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('tenantSlug');

      const searchParams = new URLSearchParams();
      if (message) {
        searchParams.set('notice', message);
      }

      window.location.href = `/plan-upgrade${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;
    }
    return Promise.reject(error);
  }
);

export default api;

// Maps known backend `detail` messages (returned in HTTPException payloads) to i18n keys so
// API error text follows the selected UI language. Unknown or non-string details pass through
// unchanged, preserving the previous behavior.
const API_ERROR_KEYS: Record<string, string> = {
  'Akış bulunamadı': 'apiErrors.flowNotFound',
  'Başvuru bulunamadı': 'apiErrors.applicationNotFound',
  'Blok bulunamadı': 'apiErrors.blockNotFound',
  'Bu case study zaten gönderilmiş': 'apiErrors.caseStudyAlreadySubmitted',
  'Bu e-posta adresi zaten kayıtlı': 'apiErrors.emailAlreadyRegistered',
  'Bu email adresi zaten kayıtlı': 'apiErrors.emailAlreadyRegistered',
  'Bu email adresi zaten kullanılıyor': 'apiErrors.emailInUse',
  'Bu işlem için yetkiniz yok': 'apiErrors.notAuthorized',
  'Bu plan için online ödeme bulunmuyor': 'apiErrors.noOnlinePaymentForPlan',
  'Bu slug zaten kullanılıyor': 'apiErrors.slugInUse',
  'Case study ataması bulunamadı': 'apiErrors.caseStudyAssignmentNotFound',
  'Case study bulunamadı': 'apiErrors.caseStudyNotFound',
  'Davet linkinin süresi dolmuş': 'apiErrors.inviteLinkExpired',
  'Davet linkinin süresi dolmuş. Lütfen yeni bir davet isteyin.': 'apiErrors.inviteLinkExpiredRequestNew',
  'Demo talebi bulunamadı': 'apiErrors.demoRequestNotFound',
  'Doğrulama kodu süresi dolmuş. Yeni kod isteyin.': 'apiErrors.verificationCodeExpired',
  'Email gönderilemedi': 'apiErrors.emailSendFailed',
  'Geçersiz davet linki': 'apiErrors.invalidInviteLink',
  'Geçersiz doğrulama kodu': 'apiErrors.invalidVerificationCode',
  'Geçersiz plan tipi': 'apiErrors.invalidPlanType',
  'Geçersiz veya süresi dolmuş davet linki': 'apiErrors.invalidOrExpiredInviteLink',
  'Gönderilmiş case study güncellenemez': 'apiErrors.submittedCaseStudyImmutable',
  'Hesap askıda. Lütfen planınızı yenileyin.': 'apiErrors.accountSuspendedRenew',
  "Kendi hesabınızı silmek için 'Hesabımı Sil' seçeneğini kullanın.": 'apiErrors.useDeleteMyAccount',
  'Kullanıcı bulunamadı': 'apiErrors.userNotFound',
  'Lütfen bir cevap veya dosya gönderin': 'apiErrors.answerOrFileRequired',
  'Mevcut mülakat limitiniz tükenmiştir.': 'apiErrors.interviewLimitExhausted',
  "Sadece gönderilmiş case study'ler değerlendirilebilir": 'apiErrors.onlySubmittedCaseStudy',
  'Son teslim tarihi geçmiş': 'apiErrors.deadlinePassed',
  'Super HR hesapları silinemez.': 'apiErrors.superHrUndeletable',
  'Tablo bulunamadı': 'apiErrors.tableNotFound',
  'Tenant bulunamadı': 'apiErrors.tenantNotFound',
  'Token bulunamadı': 'apiErrors.tokenNotFound',
  'Varsayılan akışlar düzenlenemez': 'apiErrors.defaultFlowImmutable',
  'Varsayılan akışlar silinemez': 'apiErrors.defaultFlowUndeletable',
  'Varsayılan akışlara blok eklenemez': 'apiErrors.defaultFlowNoBlockAdd',
  'Varsayılan akışlardan blok silinemez': 'apiErrors.defaultFlowNoBlockRemove',
  'Ödeme başlatılırken hata oluştu': 'apiErrors.paymentInitError',
  'Önce SMTP ayarlarınızı kaydedin.': 'apiErrors.saveSmtpFirst',
  'Şifre en az 8 karakter olmalıdır': 'apiErrors.passwordTooShort',  'Category name is required': 'apiErrors.categoryNameRequired',
  'Category name is too long': 'apiErrors.categoryNameTooLong',
  'A category with this name already exists': 'apiErrors.categoryExists',
  'Category limit reached': 'apiErrors.categoryLimitReached',
  'Category not found': 'apiErrors.categoryNotFound',
  'Category still has questions. Move or delete them first.': 'apiErrors.categoryHasQuestions',
  'Question text or expected answer is required': 'apiErrors.expectedAnswerRequired',
  'Text is too long': 'apiErrors.textTooLong',
  'Key points could not be generated right now': 'apiErrors.keyPointsUnavailable',
};

export const apiErrorText = <T,>(detail: T): T | string => {
  if (typeof detail !== 'string') return detail;
  const key = API_ERROR_KEYS[detail];
  return key ? i18n.t(key) : detail;
};
